import React from "react";
import PageFrame from "./PageFrame";
import Abs from "./Abs";
import {OFFICE_ADDRESS, STRIVE_DOMAIN} from "../bov-templates";
import {formatPhoneNumber} from "../../../utils";

/**
 * The back cover - full bleed artwork, last page of the deck.
 *
 * This page is not in the reference PDFs, which run to 10 pages (multi-tenant) and 9 (single),
 * ending on the team page. The artwork was supplied separately, one file per office and product
 * group, each named "... Back Cover Template".
 *
 * The banner is followed by a contact strip: an agent, the office address with STRIVEre.com
 * beneath it, and a second agent, divided by two green rules. Everything written on it is
 * drawn here - the artwork carries only the rules and the graded ground.
 *
 * It used to carry four cells divided by three rules, with the address over two lines and
 * STRIVEre.com in a cell of its own, both baked into the JPG. The business asked on
 * 2026-09-12 for the address on one line with the domain under it, centred between the
 * remaining rules, and none of that was reachable while it was pixels. The middle rule and
 * both text blocks were patched out of all eight assets with
 * `scripts/patch-back-cover-strip.ps1`; rerun it if a back cover is ever reissued with the
 * old strip baked back in.
 *
 * Phone numbers arrive dot-separated: Financials runs them through convertPhoneNumber before
 * handing the list over, and the old decks print them that way. The strip is drawn with
 * parentheses in the supplied artwork, so the deck puts them back with the app's own
 * formatPhoneNumber rather than changing what Financials passes, which the old decks still
 * rely on.
 *
 * **The back cover prints no page number.** The artwork's own baked number was painted out
 * when the asset was processed, and `pageNo` is deliberately not passed on, so PageFrame
 * renders nothing. `dark` went with it: it only ever selected the light page-number colour,
 * and no other rule keys off it.
 *
 * The deck's `totalPages` still counts this page, so the rest of the deck reads "/ 13" on a
 * thirteen-page deck. Only the number on this page is suppressed, not the page itself.
 */

/**
 * Where the two agent blocks sit, in page pixels, measured off the 3334 x 2500 artwork. The
 * asset is cropped to the page's own aspect when it is built, so these map linearly with no
 * CSS crop to account for.
 *
 * The strip reads: agent, address, agent. The two baked agent placeholders were shifted one
 * cell left in the artwork itself, which freed the last cell for the second agent - so this
 * list is **paired with that artwork** and both have to move together.
 *
 * The first box is centred on where the artwork's placeholder text sat rather than on the
 * midpoint between the green rules: the designer's cells are not uniformly centred. The
 * fourth cell never held a placeholder, so the second box is simply the cell the script
 * measured, 1616..2112 of 2112, halved.
 */
const AGENT_CELLS = [
    {x: 1,   w: 260},
    {x: 808, w: 248},
];

/**
 * The address block, filling what used to be the middle two cells. It spans the full width
 * between the two surviving rules - which sit at x=259 and x=806 on seven of the eight
 * covers, and 257 and 806 on houston-industrial - so its text centres on the strip's own
 * midpoint rather than on either old cell.
 */
const ADDRESS_CELL = {x: 262, w: 541};

/** Top of the first text line; the second follows at the strip's own 25px pitch. */
const AGENTS_Y = 740;

export default function BackCover({template, agents}) {
    const list = (agents || []).slice(0, AGENT_CELLS.length);
    // One line, as the front cover writes it: "17742 Preston Rd., Dallas, TX 75252".
    const address = (OFFICE_ADDRESS[template.officeLocation] || []).join(', ');

    return (
        <PageFrame bleed>
            <img className='bov-cover__img' src={template.backCover} alt='' loading='eager'/>

            <Abs x={ADDRESS_CELL.x} y={AGENTS_Y} w={ADDRESS_CELL.w}>
                <div className='bov-cover__agent'>
                    {address ? <div>{address}</div> : null}
                    <div>{STRIVE_DOMAIN}</div>
                </div>
            </Abs>

            {AGENT_CELLS.map((cell, i) => {
                const agent = list[i];
                if (!agent) return null;
                return (
                    <Abs key={i} x={cell.x} y={AGENTS_Y} w={cell.w}>
                        <div className='bov-cover__agent'>
                            <div>{agent.name}</div>
                            {agent.phone ? <div>{formatPhoneNumber(agent.phone)}</div> : null}
                        </div>
                    </Abs>
                );
            })}
        </PageFrame>
    );
}
